import { databases, ID, storage, ensureAuth } from './appwriteClient';
import { Student } from '../types';
import { Query, Permission, Role } from 'appwrite';
import { createAttendanceAlert } from './attendanceService';

// Type for student data that can include File objects for uploads
export interface StudentDataWithFiles {
  name: string;
  email: string;
  register_num: string;
  dob: string;
  gender: 'male' | 'female';
  student_photo_url: string | File | null;
  birth_certificate: string | File | null;
  reg_no: string;
  address: string;
  fathers_name: string;
  father_num: string;
  father_photo_url: string | File | null;
  mothers_name: string;
  mother_num: string;
  mother_photo_url: string | File | null;
  family_photo_url: string | File | null;
  guardian_name: string;
  guardian_photo_url: string | File | null;
  special_instruction: string;
  allergies: string;
  cab: 'yes' | 'no';
  cab_loc: string;
  day_care: 'yes' | 'no';
  day_care_time: string;
  level: string;
  group: string;
  total_fees: string;
  paid_fees?: string;
  total_day_care_fees?: string;
  paid_day_care_fees?: string;
  total_cab_fees?: string;
  paid_cab_fees?: string;
}

// Database and collection IDs
const DATABASE_ID = '682acd26001f69683cd8';
const STUDENT_PROFILE_COLLECTION_ID = '682acd33000e34dd93c0';

// Bucket ID for photos - this is the correct bucket ID for all photos
const PHOTOS_BUCKET_ID = '6831d6dd0022cb959e72';

// Helper function to map Appwrite document to Student type
export const mapDocumentToStudent = (doc: any): Student => {
  return {
    id: doc.$id,
    name: doc.name,
    email: doc.email,
    register_num: doc.roll_num,
    dob: doc.DOB,
    gender: doc.gender,
    address: doc.address || '',
    reg_no: doc.Register_number,
    father_num: doc.father_num || '',
    mother_num: doc.mother_num || '',
    level: typeof doc.level === 'string' ? doc.level : String(doc.level || '1'),
    fathers_name: doc.father_name,
    mothers_name: doc.mother_name,
    guardian_name: doc.guardian_name,
    student_photo_url: doc.student_photo_url || '',
    father_photo_url: doc.father_photo_url || '',
    mother_photo_url: doc.mother_photo_url || '',
    family_photo_url: doc.family_photo_url || '',
    guardian_photo_url: doc.guardian_photo_url || '',
    birth_certificate: doc.birth_certificate || '',
    day_care_time: doc.day_care_time || '',
    group: doc.group || 'larva1',
    cab: doc.cab === 'yes' || doc.cab === 'no' ? doc.cab : 'no',
    cab_loc: doc.cab_loc || '',
    day_care: doc.day_care === 'yes' || doc.day_care === 'no' ? doc.day_care : 'no',
    special_instruction: doc.special_instruction || '',
    allergies: doc.allergies || '',
    attendance: Array.isArray(doc.attendance) ? doc.attendance : undefined,
    
    total_fees: typeof doc.total_fees === 'number' ? String(doc.total_fees) : (doc.total_fees || '0'),
    paid_fees: typeof doc.paid_fees === 'number' ? String(doc.paid_fees) : (doc.paid_fees || '0'),

    paid_day_care_fees: typeof doc.paid_day_care_fees === 'number' ? String(doc.paid_day_care_fees) : (doc.paid_day_care_fees || '0'),
    total_day_care_fees: typeof doc.total_day_care_fees === 'number' ? String(doc.total_day_care_fees) : (doc.total_day_care_fees || '0'),
    total_cab_fees: typeof doc.total_cab_fees === 'number' ? String(doc.total_cab_fees) : (doc.total_cab_fees || '0'),
    paid_cab_fees: typeof doc.paid_cab_fees === 'number' ? String(doc.paid_cab_fees) : (doc.paid_cab_fees || '0'),
    transactions: doc.transactions || [],
  };
};

// Helper function to upload file
async function uploadFile(file: File, type: string): Promise<string> {
  try {
    console.log(`Starting upload for ${type}...`);
    console.log('File details:', {
      name: file.name,
      type: file.type,
      size: file.size
    });
    console.log(`Uploading to bucket: ${PHOTOS_BUCKET_ID}`);
    
    // Ensure the file is valid
    if (!file || !(file instanceof File)) {
      console.error('Invalid file:', file);
      throw new Error(`Invalid file for ${type}`);
    }

    // Create a unique file ID
    const fileId = ID.unique();
    console.log('Generated file ID:', fileId);
    
    // Upload the file with proper permissions
    const response = await storage.createFile(
      PHOTOS_BUCKET_ID,
      fileId,
      file,
      [Permission.read(Role.any()), Permission.write(Role.any())]
    );

    if (!response || !response.$id) {
      console.error('Upload failed - no response ID:', response);
      throw new Error(`Failed to upload ${type} file: No response ID`);
    }

    console.log(`${type} file uploaded successfully:`, {
      fileId: response.$id,
      bucketId: PHOTOS_BUCKET_ID,
      type: type
    });
    return response.$id;
  } catch (error) {
    console.error(`Error uploading ${type} file:`, error);
    if (error instanceof Error) {
      throw new Error(`Failed to upload ${type}: ${error.message}`);
    }
    throw new Error(`Failed to upload ${type} file`);
  }
}

// Helper function to get file URL
export const getFileUrl = (fileId: string | null | undefined): string | null => {
  if (!fileId) {
    console.log('No file ID provided');
    return null;
  }
  try {
    console.log('Getting file URL for ID:', fileId);
    // Use the Appwrite client to get the file URL with proper permissions
    const url = storage.getFileView(
      PHOTOS_BUCKET_ID,
      fileId
    );
    console.log('Generated file URL:', url);
    return url.toString();
  } catch (error) {
    console.error('Error getting file URL:', error);
    return null;
  }
};

// Helper function to get file preview URL
export const getFilePreviewUrl = (fileId: string | null | undefined): string | null => {
  if (!fileId) {
    console.log('No file ID provided for preview');
    return null;
  }
  try {
    console.log('Getting file preview URL for ID:', fileId);
    // Use the Appwrite client to get the file preview URL with proper permissions
    const url = storage.getFilePreview(
      PHOTOS_BUCKET_ID,
      fileId,
      300,
      300
    );
    console.log('Generated file preview URL:', url);
    return url.toString();
  } catch (error) {
    console.error('Error getting file preview URL:', error);
    return null;
  }
};

// Helper to check if value is a File (browser or Node)
function isFile(val: any): val is File {
  return val && typeof val === 'object' && typeof val.name === 'string' && typeof val.size === 'number';
}

export async function getStudents(): Promise<Student[]> {
  try {
    await ensureAuth();
    console.log('Fetching students from:', DATABASE_ID, STUDENT_PROFILE_COLLECTION_ID);
    
    // First, get the total count of documents
    const countResponse = await databases.listDocuments(
      DATABASE_ID,
      STUDENT_PROFILE_COLLECTION_ID,
      [
        Query.limit(1)
      ]
    );
    
    const total = countResponse.total;
    console.log('Total number of students in database:', total);
    
    // Then fetch all students with a high limit and no filters
    const res = await databases.listDocuments(
      DATABASE_ID,
      STUDENT_PROFILE_COLLECTION_ID,
      [
        Query.limit(100),
        Query.offset(0)
      ]
    );
    
    if (!res || !res.documents) {
      console.error('Invalid response from Appwrite:', res);
      throw new Error('Invalid response from server');
    }
    
    console.log('Successfully fetched students:', res.documents.length);
    console.log('Response total:', res.total);
    
    // Log all student IDs and names for debugging
    console.log('All students:', res.documents.map(doc => ({
      id: doc.$id,
      name: doc.name,
      group: doc.group,
      level: doc.level
    })));
    
    const mappedStudents = res.documents.map(mapDocumentToStudent);
    console.log('Mapped students count:', mappedStudents.length);
    console.log('Groups in mapped students:', [...new Set(mappedStudents.map(s => s.group))]);
    
    // Verify we got all students
    if (mappedStudents.length !== total) {
      console.warn(`Warning: Fetched ${mappedStudents.length} students but total is ${total}`);
      // If we didn't get all students, try fetching again with a higher limit
      if (mappedStudents.length < total) {
        console.log('Attempting to fetch all students with higher limit...');
        const retryResponse = await databases.listDocuments(
          DATABASE_ID,
          STUDENT_PROFILE_COLLECTION_ID,
          [
            Query.limit(200), // Try with a higher limit
            Query.offset(0)
          ]
        );
        
        if (retryResponse.documents.length > mappedStudents.length) {
          console.log('Successfully fetched additional students:', retryResponse.documents.length - mappedStudents.length);
          return retryResponse.documents.map(mapDocumentToStudent);
        }
      }
    }
    
    return mappedStudents;
  } catch (error) {
    console.error('Error fetching students:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to fetch students: ${error.message}`);
    }
    throw new Error('Failed to fetch students: Unknown error');
  }
}

export async function addStudent(studentData: StudentDataWithFiles): Promise<Student> {
  try {
    await ensureAuth(); // Ensure user is authenticated

    // Validate email format
    if (studentData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(studentData.email)) {
      throw new Error('Invalid email format');
    }

    // Upload files to Appwrite Storage only if they are File objects
    let studentPhotoId = '';
    let fatherPhotoId = '';
    let motherPhotoId = '';
    let familyPhotoId = '';
    let guardianPhotoId = '';
    let birthCertificateId = '';

    try {
      if (isFile(studentData.student_photo_url)) {
        studentPhotoId = await uploadFile(studentData.student_photo_url, 'student photo');
      }

      if (isFile(studentData.father_photo_url)) {
        fatherPhotoId = await uploadFile(studentData.father_photo_url, 'father photo');
      }

      if (isFile(studentData.mother_photo_url)) {
        motherPhotoId = await uploadFile(studentData.mother_photo_url, 'mother photo');
      }

      if (isFile(studentData.family_photo_url)) {
        familyPhotoId = await uploadFile(studentData.family_photo_url, 'family photo');
      }

      if (isFile(studentData.guardian_photo_url)) {
        guardianPhotoId = await uploadFile(studentData.guardian_photo_url, 'guardian photo');
      }

      if (isFile(studentData.birth_certificate)) {
        birthCertificateId = await uploadFile(studentData.birth_certificate, 'birth certificate');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      throw new Error('Failed to upload photos. Please check your internet connection and try again.');
    }

    // Format date of birth
    let dob = null;
    if (studentData.dob) {
      try {
        const date = new Date(studentData.dob);
        if (!isNaN(date.getTime())) {
          // Format date as YYYY-MM-DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          dob = `${year}-${month}-${day}`;
          console.log('Formatted date of birth:', dob);
        } else {
          console.error('Invalid date format:', studentData.dob);
          throw new Error('Invalid date format');
        }
      } catch (error) {
        console.error('Error formatting date:', error);
        throw new Error('Invalid date format');
      }
    }

    // Transform the data to match Appwrite schema
    const appwriteData = {
      name: studentData.name || '',
      email: studentData.email || '',
      roll_num: studentData.register_num || '',
      DOB: dob || '',
      gender: studentData.gender || 'male',
      address: studentData.address || '',
      Register_number: studentData.reg_no || '',
      father_num: studentData.father_num || '',
      mother_num: studentData.mother_num || '',
      level: studentData.level || '1',
      father_name: studentData.fathers_name || '',
      mother_name: studentData.mothers_name || '',
      guardian_name: studentData.guardian_name || '',
      student_photo_url: studentPhotoId || '',
      father_photo_url: fatherPhotoId || '',
      mother_photo_url: motherPhotoId || '',
      family_photo_url: familyPhotoId || '',
      guardian_photo_url: guardianPhotoId || '',
      birth_certificate: birthCertificateId || '',
      day_care_time: studentData.day_care_time || '',
      group: studentData.group || 'larva1',
      cab: studentData.cab || 'no',
      cab_loc: studentData.cab_loc || '',
      day_care: studentData.day_care || false,
      special_instruction: studentData.special_instruction || '',
      Allergies: studentData.allergies || '',
      total_fees: typeof studentData.total_fees === 'number' ? String(studentData.total_fees) : (studentData.total_fees || '0'),
      paid_fees: typeof studentData.paid_fees === 'number' ? String(studentData.paid_fees) : (studentData.paid_fees || '0'),
      total_day_care_fees: typeof studentData.total_day_care_fees === 'number' ? String(studentData.total_day_care_fees) : (studentData.total_day_care_fees || '0'),
      total_cab_fees: typeof studentData.total_cab_fees === 'number' ? String(studentData.total_cab_fees) : (studentData.total_cab_fees || '0'),
    };

    // Remove undefined values
    (Object.keys(appwriteData) as (keyof typeof appwriteData)[]).forEach(key => {
      if (appwriteData[key] === undefined) {
        delete appwriteData[key];
      }
    });

    console.log('Creating student document with data:', appwriteData);

    const response = await databases.createDocument(
      DATABASE_ID,
      STUDENT_PROFILE_COLLECTION_ID,
      ID.unique(),
      appwriteData
    );

    console.log('Student document created successfully:', response);
    return mapDocumentToStudent(response);
  } catch (error) {
    console.error('Error adding student:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to add student: ${error.message}`);
    }
    throw new Error('Failed to add student: Unknown error');
  }
}

export const updateStudent = async (studentId: string, studentData: Partial<StudentDataWithFiles>): Promise<Student> => {
  try {
    await ensureAuth(); // Ensure user is authenticated
    
    console.log('Updating student with ID:', studentId);
    console.log('Raw student data to update:', studentData);

    // Handle file uploads
    let studentPhotoId = '';
    let fatherPhotoId = '';
    let motherPhotoId = '';
    let familyPhotoId = '';
    let guardianPhotoId = '';
    let birthCertificateId = '';

    try {
      // Log file objects for debugging
      console.log('Checking files for upload:');
      console.log('Student photo:', isFile(studentData.student_photo_url) ? 'File object' : 'Not a file');
      console.log('Father photo:', isFile(studentData.father_photo_url) ? 'File object' : 'Not a file');
      console.log('Mother photo:', isFile(studentData.mother_photo_url) ? 'File object' : 'Not a file');
      console.log('Family photo:', isFile(studentData.family_photo_url) ? 'File object' : 'Not a file');
      console.log('Guardian photo:', isFile(studentData.guardian_photo_url) ? 'File object' : 'Not a file');
      console.log('Birth certificate:', isFile(studentData.birth_certificate) ? 'File object' : 'Not a file');

      // Upload files if they are File objects
      if (isFile(studentData.student_photo_url)) {
        console.log('Uploading student photo...');
        studentPhotoId = await uploadFile(studentData.student_photo_url, 'student photo');
        console.log('Student photo uploaded with ID:', studentPhotoId);
      } else {
        studentPhotoId = studentData.student_photo_url || '';
      }

      if (isFile(studentData.father_photo_url)) {
        console.log('Uploading father photo...');
        fatherPhotoId = await uploadFile(studentData.father_photo_url, 'father photo');
        console.log('Father photo uploaded with ID:', fatherPhotoId);
      } else {
        fatherPhotoId = studentData.father_photo_url || '';
      }

      if (isFile(studentData.mother_photo_url)) {
        console.log('Uploading mother photo...');
        motherPhotoId = await uploadFile(studentData.mother_photo_url, 'mother photo');
        console.log('Mother photo uploaded with ID:', motherPhotoId);
      } else {
        motherPhotoId = studentData.mother_photo_url || '';
      }

      if (isFile(studentData.family_photo_url)) {
        console.log('Uploading family photo...');
        familyPhotoId = await uploadFile(studentData.family_photo_url, 'family photo');
        console.log('Family photo uploaded with ID:', familyPhotoId);
      } else {
        familyPhotoId = studentData.family_photo_url || '';
      }

      if (isFile(studentData.guardian_photo_url)) {
        console.log('Uploading guardian photo...');
        guardianPhotoId = await uploadFile(studentData.guardian_photo_url, 'guardian photo');
        console.log('Guardian photo uploaded with ID:', guardianPhotoId);
      } else {
        guardianPhotoId = studentData.guardian_photo_url || '';
      }

      if (isFile(studentData.birth_certificate)) {
        console.log('Uploading birth certificate...');
        birthCertificateId = await uploadFile(studentData.birth_certificate, 'birth certificate');
        console.log('Birth certificate uploaded with ID:', birthCertificateId);
      } else {
        birthCertificateId = studentData.birth_certificate || '';
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      throw new Error('Failed to upload photos. Please check your internet connection and try again.');
    }

    // Format date of birth
    let dob = null;
    if (studentData.dob) {
      try {
        const date = new Date(studentData.dob);
        if (!isNaN(date.getTime())) {
          // Format date as YYYY-MM-DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          dob = `${year}-${month}-${day}`;
          console.log('Formatted date of birth:', dob);
        } else {
          console.error('Invalid date format:', studentData.dob);
          throw new Error('Invalid date format');
        }
      } catch (error) {
        console.error('Error formatting date:', error);
        throw new Error('Invalid date format');
      }
    }

    // Transform the data to match Appwrite schema
    const appwriteData = {
      name: studentData.name || '',
      email: studentData.email || '',
      roll_num: studentData.register_num || '',
      DOB: dob || '',
      gender: studentData.gender || 'male',
      address: studentData.address || '',
      Register_number: studentData.reg_no || '',
      father_num: studentData.father_num || '',
      mother_num: studentData.mother_num || '',
      level: studentData.level || '1',
      father_name: studentData.fathers_name || '',
      mother_name: studentData.mothers_name || '',
      guardian_name: studentData.guardian_name || '',
      student_photo_url: studentPhotoId,
      father_photo_url: fatherPhotoId,
      mother_photo_url: motherPhotoId,
      family_photo_url: familyPhotoId,
      guardian_photo_url: guardianPhotoId,
      birth_certificate: birthCertificateId,
      day_care_time: studentData.day_care_time || '',
      group: studentData.group || 'butterfly',
      cab: studentData.cab || 'no',
      cab_loc: studentData.cab_loc || '',
      day_care: studentData.day_care || 'no',
      special_instruction: studentData.special_instruction || '',
      Allergies: studentData.allergies || '',
      total_fees: typeof studentData.total_fees === 'number' ? String(studentData.total_fees) : (studentData.total_fees || '0'),
      paid_fees: typeof studentData.paid_fees === 'number' ? String(studentData.paid_fees) : (studentData.paid_fees || '0'),
      total_day_care_fees: typeof studentData.total_day_care_fees === 'number' ? String(studentData.total_day_care_fees) : (studentData.total_day_care_fees || '0'),
      total_cab_fees: typeof studentData.total_cab_fees === 'number' ? String(studentData.total_cab_fees) : (studentData.total_cab_fees || '0'),
    };

    // Log the final data being sent to Appwrite
    console.log('Final data being sent to Appwrite:', appwriteData);

    // Remove undefined values
    (Object.keys(appwriteData) as (keyof typeof appwriteData)[]).forEach(key => {
      if (appwriteData[key] === undefined) {
        delete appwriteData[key];
      }
    });

    const response = await databases.updateDocument(
      DATABASE_ID,
      STUDENT_PROFILE_COLLECTION_ID,
      studentId,
      appwriteData
    );

    console.log('Update response:', response);
    return mapDocumentToStudent(response);
  } catch (error) {
    console.error('Detailed error updating student:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update student: ${error.message}`);
    }
    throw new Error('Failed to update student: Unknown error');
  }
};

export async function deleteStudent(id: string): Promise<void> {
  try {
    await ensureAuth();
    await databases.deleteDocument(
      DATABASE_ID,
      STUDENT_PROFILE_COLLECTION_ID,
      id
    );
    console.log('Student deleted successfully');
  } catch (error) {
    console.error('Error deleting student:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to delete student: ${error.message}`);
    }
    throw new Error('Failed to delete student: Unknown error');
  }
}

// Helper function to validate student data
export function validateStudentData(data: any): string[] {
  const errors: string[] = [];

  // Required fields
  if (!data.name) errors.push('Name is required');
  if (!data.register_num) errors.push('Register number is required');
  if (!data.dob) errors.push('Date of birth is required');
  if (!data.gender) errors.push('Gender is required');
  if (!data.level) errors.push('Level is required');

  // Validate email format if provided
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Invalid email format');
  }

  // Validate phone numbers if provided
  if (data.reg_no && !/^\d{10}$/.test(data.reg_no)) {
    errors.push('Invalid registered mobile number');
  }
  if (data.father_num && !/^\d{10}$/.test(data.father_num)) {
    errors.push('Invalid father\'s mobile number');
  }
  if (data.mother_num && !/^\d{10}$/.test(data.mother_num)) {
    errors.push('Invalid mother\'s mobile number');
  }

  // Validate date format
  if (data.dob && !/^\d{4}-\d{2}-\d{2}$/.test(data.dob)) {
    errors.push('Invalid date of birth format (use YYYY-MM-DD)');
  }

  return errors;
}

export async function makePayment(feeId: string, paymentMethod: string, amount: number) {
  try {
    // For now, simulate a successful payment
    return { success: true, message: 'Payment successful' };
  } catch (error) {
    throw new Error('Payment failed');
  }
}

export async function getStudentFees(studentId: string) {
  try {
    const res = await databases.listDocuments(
      DATABASE_ID,
      'fees', // Replace with your fees collection ID
      [
        // Query for fees matching the student ID
        // Adjust the query based on your database structure
      ]
    );
    return res.documents;
  } catch (error) {
    throw new Error('Failed to fetch student fees');
  }
}

// Add a new function to get total student count
export async function getTotalStudentCount(): Promise<number> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      STUDENT_PROFILE_COLLECTION_ID,
      [
        Query.limit(1)
      ]
    );
    console.log('Total student count from Appwrite:', response.total);
    return response.total;
  } catch (error) {
    console.error('Error getting total student count:', error);
    throw error;
  }
}

// Mark attendance for a single student
export async function markAttendance(
  studentId: string,
  date: string,
  status: 'present' | 'absent' | 'late',
  markedBy: string,
  remarks?: string
): Promise<void> {
  try {
    await ensureAuth();
    
    // Create attendance record in the attendance collection
    const ATTENDANCE_COLLECTION_ID = '683078a400301e04d0e8';
    
    await databases.createDocument(
      DATABASE_ID,
      ATTENDANCE_COLLECTION_ID,
      ID.unique(),
      {
        student_id: studentId,
        date: date,
        Status: status,
        remarks: remarks || '',
        marked_by: markedBy,
        Created_at: new Date().toISOString(),
        Updated_at: new Date().toISOString()
      },
      [
        Permission.read(Role.any()),
        Permission.write(Role.any())
      ]
    );
    
    // Create attendance alert if student is absent
    if (status === 'absent') {
      try {
        await createAttendanceAlert(studentId, date);
      } catch (error) {
        console.error('Error creating attendance alert:', error);
      }
    }
    console.log(`Attendance marked for student ${studentId}: ${status} on ${date}`);
  } catch (error) {
    console.error('Error marking attendance:', error);
    throw error;
  }
}